import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  type GrantListParams,
  type GrantsMatrixBody,
  type UpsertGrantBody,
  adminClient,
} from '../lib/admin-client.js';

export const GRANT_KEYS = {
  all: (tenantId: string) => ['admin-grants', tenantId] as const,
  list: (tenantId: string, params: GrantListParams) =>
    ['admin-grants', tenantId, 'list', params] as const,
};

export function useAdminGrants(tenantId: string, params: GrantListParams = {}) {
  return useQuery({
    queryKey: GRANT_KEYS.list(tenantId, params),
    queryFn: () => adminClient.listGrants(tenantId, params),
    enabled: tenantId.length > 0,
    staleTime: 30_000,
  });
}

export function useUpsertGrant(tenantId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: UpsertGrantBody) => adminClient.upsertGrant(tenantId, body),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: GRANT_KEYS.all(tenantId) });
    },
  });
}

export function useDeleteGrant(tenantId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (grantId: string) => adminClient.deleteGrant(tenantId, grantId),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: GRANT_KEYS.all(tenantId) });
    },
  });
}

export function useGrantsMatrix(tenantId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: GrantsMatrixBody) => adminClient.applyGrantsMatrix(tenantId, body),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: GRANT_KEYS.all(tenantId) });
    },
  });
}
